import type {
  Designation,
  GetDesignationListParams,
  GetDesignationListResponse,
} from "@/types/designation";
import api from "./client";
import type { ApiResponse } from "@gokrew/models/generic";

export async function getDesignationList(companyId: string, params: GetDesignationListParams) {
  const { data } = await api.get<GetDesignationListResponse>(
    `/companies/${companyId}/designations`,
    {
      params,
    }
  );
  return data;
}

/**
 * Create a new designation in the company
 *
 * @param {string} companyId - The id of the company
 * @param {Partial<Designation>} data - The designation data to be created
 * @returns {Promise<Designation>} - The created designation
 */
export async function createDesignation(
  companyId: string,
  data: Partial<Designation>
) {
  const res = await api.post<ApiResponse<Designation>>(
    `/companies/${companyId}/designations`,
    data
  );
  return res.data;
}

/**
 * Update an existing designation in the company
 *
 * @param {string} companyId - The id of the company
 * @param {string} designationId - The id of the designation to be updated
 * @param {Partial<Designation>} data - The designation data to be updated
 * @returns {Promise<Designation>} - The updated designation
 */
export async function updateDesignation(
  companyId: string,
  designationId: string,
  data: Partial<Designation>
) {
  const res = await api.put<ApiResponse<Designation>>(
    `/companies/${companyId}/designations/${designationId}`,
    data
  );
  return res.data;
}

/**
 * Update the status of a designation
 *
 * @param {string} companyId - The id of the company
 * @param {string} designationId - The id of the designation to be updated
 */
export async function updateDesignationStatus(
  companyId: string,
  designationId: string
) {
  const res = await api.patch(
    `/companies/${companyId}/designations/${designationId}/status`
  );
  return res.data;
}

/**
 * Delete an existing designation in the company
 *
 * @param {string} companyId - The id of the company
 * @param {string} designationId - The id of the designation to be deleted
 *
 * @note This API is yet to be implemented from the backend
 */
export async function deleteDesignation(
  companyId: string,
  designationId: string
) {
  const res = await api.delete(
    `/companies/${companyId}/designations/${designationId}`
  );
  return res.data;
}
